import React, { useState } from 'react';
import { ModelArtifactTable } from 'src/components/ModelArtifactTable';
import { ProfileDetails } from 'src/components/ProfileDetails';
import { TriggerWorkflowModal } from 'src/components/TriggerWorkflowModal';
import { useParams } from 'react-router-dom';
import { ModelVersionModal } from 'src/components/ModelVersionModal';
import { getModelVersion } from 'src/hooks/useModelVersions';
import { Placeholder } from 'src/components/Placeholder';
import { ModelVersionEventsTable } from 'src/components/ModelVersionEventsTable';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';
import { PermissionAwareButtonDropdown } from 'src/components/PermissionAwareButtonDropdown';
import {
  ButtonDropdownProps,
  Container,
  KeyValuePairs,
  SpaceBetween,
} from '@cloudscape-design/components';

export function ModelVersionDetails() {
  const [modelVersionModalVisible, setModelVersionModalVisible] =
    useState<boolean>(false);
  const [modelVersionModalMode, setModelVersionModalMode] = useState<
    'New' | 'Edit' | 'Delete'
  >('New');
  const [modelVersionModalValues, setModelVersionModalValues] =
    useState<any>(null);

  const showModelVersionModal = (
    mode: 'New' | 'Edit' | 'Delete',
    values: any,
  ) => {
    setModelVersionModalValues(values);
    setModelVersionModalMode(mode);
    setModelVersionModalVisible(true);
  };

  const { accountNo, applicationId, modelVersionId } = useParams();

  const { data, isLoading } = getModelVersion({
    modelVersionId: modelVersionId,
  });

  const [triggerWorkflowModalVisible, setTriggerWorkflowModalVisible] =
    useState<boolean>(false);

  const actionHandler = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    switch (event.detail.id) {
      case 'edit_model_version':
        if (!data) return;
        showModelVersionModal('Edit', {
          id: modelVersionId,
          applicationId,
          name: data.name,
          description: data.description,
          version: data.version,
        });
        break;
      case 'delete_model_version':
        showModelVersionModal('Delete', { id: modelVersionId, applicationId });
        break;
    }
  };

  return (
    <div>
      <div className="model-version-details__header">
        <h2>Model Version Details</h2>
        <div className="model-version-details__button-group">
          <PermissionAwareButton
            requiredPermission="READ_AND_LIMITED_ACTION"
            actionDescription="trigger workflows"
            variant="normal"
            onClick={() => {
              setTriggerWorkflowModalVisible(true);
            }}
          >
            Trigger Workflow
          </PermissionAwareButton>
          <PermissionAwareButtonDropdown
            variant="normal"
            items={[
              {
                text: 'Edit model version',
                id: 'edit_model_version',
                requiredPermission: 'ADMINISTER',
                actionDescription: 'edit model versions',
              },
              {
                text: 'Delete model version',
                id: 'delete_model_version',
                requiredPermission: 'ADMINISTER',
                actionDescription: 'delete model versions',
              },
            ]}
            onItemClick={(e) => actionHandler(e)}
          >
            Manage Model Version
          </PermissionAwareButtonDropdown>
        </div>
      </div>

      <SpaceBetween size="xl">
        <Container>
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Name', value: data?.name || '-' },
              { label: 'Id', value: data?.id || '-' },
              { label: 'Description', value: data?.description || '-' },
              { label: 'Version', value: data?.version || '1' },
            ]}
          />
        </Container>

        <ModelVersionEventsTable />
        <ModelArtifactTable />
      </SpaceBetween>

      <div className="profile-details">
        <Placeholder
          shapes={
            <>
              <rect y="0" width="90" height="25" />
              <rect x="30%" y="0" width="90" height="25" />
              <rect x="60%" y="0" width="90" height="25" />
              <rect x="80%" y="0" width="90" height="25" />
            </>
          }
          ready={!isLoading}
        >
          <ProfileDetails
            createdBy={data?.createdBy || ''}
            updatedBy={data?.updatedBy || ''}
            createdDate={data?.createdDate || ''}
            updatedDate={data?.updatedDate || ''}
          />
        </Placeholder>
      </div>
      {triggerWorkflowModalVisible && (
        <TriggerWorkflowModal
          onClose={() => {
            setTriggerWorkflowModalVisible(false);
          }}
        />
      )}
      {applicationId && (
        <ModelVersionModal
          visible={modelVersionModalVisible}
          modalMode={modelVersionModalMode}
          values={modelVersionModalValues}
          onClose={() => {
            setModelVersionModalVisible(false);
          }}
          applicationId={applicationId}
          redirectLocation={
            modelVersionModalMode === 'Delete'
              ? `/accounts/${accountNo}/applications/${applicationId}`
              : undefined
          }
        />
      )}
    </div>
  );
}
